import React from 'react';
import { Link } from 'react-router-dom';
import SiteShell from './SiteShell';

const UPDATED = '22 August 2026';

/**
 * Privacy policy.
 *
 * Written to describe what the platform actually does — OTP sign-in, GSTIN
 * verification, courier handover, payment through Razorpay — rather than
 * generic boilerplate, because Google Play checks that the policy matches the
 * permissions and data the app declares.
 */
const Privacy = () => (
  <SiteShell>
    <div className="site-wrap site-legal">
      <h1>Privacy policy</h1>
      <p className="updated">Last updated {UPDATED}</p>

      <p>
        Tarpex.live (“Tarpex”, “we”) is a business-to-business marketplace for the tarpaulin
        trade, operated by Stackerio. This policy explains what we collect, why, who we share it
        with, and how you can have it removed.
      </p>

      <h2>1. What we collect</h2>
      <ul>
        <li>
          <b>Your identity and contact details</b> — mobile number (used to sign in), name, and
          email address if you give one.
        </li>
        <li>
          <b>Your business details</b> — company name, GSTIN, PAN, and the addresses you add for
          delivery or pickup. We verify the GSTIN is valid and active.
        </li>
        <li>
          <b>Trading activity</b> — products you list or buy, orders, invoices, returns,
          cancellations and support requests.
        </li>
        <li>
          <b>Payment records</b> — the amount, status and gateway reference for each payment.
          Card and UPI details are handled by our payment gateway and never reach our servers.
        </li>
        <li>
          <b>Photos you upload</b> — product images, claim evidence and support attachments,
          taken from your gallery or camera with your permission.
        </li>
        <li>
          <b>Device and usage data</b> — device token for notifications, app version, and basic
          logs used to diagnose faults.
        </li>
      </ul>

      <h2>2. Why we use it</h2>
      <ul>
        <li>To sign you in and keep your account secure.</li>
        <li>To verify that a business is real and entitled to trade, using its GSTIN.</li>
        <li>To place, price, tax and fulfil orders, and to raise correct invoices.</li>
        <li>To arrange collection and delivery with the courier.</li>
        <li>To collect payments and settle sellers.</li>
        <li>To notify you about your orders, returns and support requests.</li>
        <li>To meet our obligations under Indian tax and company law.</li>
      </ul>

      <h2>3. Camera and photo access</h2>
      <p>
        The app asks for camera and photo access only when you choose to add an image — to a
        product listing, a claim or a support request. We do not access your camera or gallery in
        the background, and declining the permission leaves every other part of the app working.
      </p>

      <h2>4. Who we share it with</h2>
      <p>We share only what a task requires, and only with:</p>
      <ul>
        <li>
          <b>The other party to your order.</b> A seller sees the buying company's name, delivery
          address, contact number and GSTIN; a buyer sees the seller's business details. This is
          unavoidable — it is what the invoice and the consignment note carry.
        </li>
        <li><b>Our courier partner</b>, to collect and deliver the consignment.</li>
        <li><b>Our payment gateway</b>, to take payment and issue refunds.</li>
        <li><b>Communication providers</b>, to send SMS, WhatsApp and email notifications.</li>
        <li><b>Government authorities</b>, where the law requires it.</li>
      </ul>
      <p>We do not sell your data, and we do not use it for third-party advertising.</p>

      <h2>5. How long we keep it</h2>
      <p>
        Trading records — orders, invoices, payments and GST returns — are retained for eight
        years, as Indian tax and company law require. Personal details not tied to those records
        are removed when you delete your account.
      </p>

      <h2>6. Your rights</h2>
      <ul>
        <li>See and correct your details from Profile in the app.</li>
        <li>Turn off notifications from your device settings.</li>
        <li>Ask us for a copy of your data at support@tarpex.live.</li>
        <li>Delete your account — see below.</li>
      </ul>

      <div className="callout">
        <b>Deleting your account</b>
        <p style={{ margin: '8px 0 14px' }}>
          You can request deletion from the app, or from the web without installing anything.
          We verify it is you by SMS, then remove your personal details within 30 days.
        </p>
        <Link className="site-btn site-btn-primary" to="/delete-account">
          Delete your account
        </Link>
      </div>

      <h2>7. Security</h2>
      <p>
        Access is protected by one-time passcodes rather than reusable passwords. Data is
        transmitted over TLS, payment signatures are verified server-side before an order is
        marked paid, and third-party credentials are stored encrypted and never exposed to the
        app.
      </p>

      <h2>8. Children</h2>
      <p>
        Tarpex is a platform for registered businesses and is not directed at anyone under 18.
        We do not knowingly collect data from children.
      </p>

      <h2>9. Changes</h2>
      <p>
        If we change this policy we will update the date above and, where the change is
        significant, notify you in the app.
      </p>

      <h2>10. Contact</h2>
      <p>
        Questions or complaints: <a href="mailto:support@tarpex.live">support@tarpex.live</a>.
        We reply within one working day.
      </p>
    </div>
  </SiteShell>
);

export default Privacy;
